import { JiraApprovedProjects } from "@/components/settings/JiraApprovedProjects";
import { JiraForgeStep } from "@/components/settings/JiraForgeStep";
import { JiraWorkHealthSection } from "@/components/settings/JiraWorkHealth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useJiraConnection, useJiraMutations, useOrg } from "@/hooks";
import { parseJiraSiteUrl } from "@/lib/jira";
import { ApiError } from "@/services";
import { useEffect, useRef, useState } from "react";

export function JiraIntegrationCard() {
	const org = useOrg();
	const connection = useJiraConnection();
	const { connect, disconnect } = useJiraMutations();
	const [siteUrl, setSiteUrl] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [awaitingAuthorization, setAwaitingAuthorization] = useState(false);
	const [checkingInstall, setCheckingInstall] = useState(false);
	const popupRef = useRef<Window | null>(null);
	const previousExpiryRef = useRef<string | null>(null);
	const canEdit =
		org.data?.org.role === "owner" || org.data?.org.role === "admin";
	const status = connection.data;
	const expired =
		status?.connected === true &&
		new Date(status.webhookExpiresAt).getTime() <= Date.now();

	useEffect(() => {
		if (!awaitingAuthorization) return;
		let active = true;
		let inFlight = false;
		const check = async () => {
			if (!active || inFlight) return;
			inFlight = true;
			try {
				const result = await connection.refetch();
				if (
					active &&
					result.data?.connected &&
					(!previousExpiryRef.current ||
						result.data.webhookExpiresAt !== previousExpiryRef.current)
				) {
					popupRef.current?.close();
					popupRef.current = null;
					setAwaitingAuthorization(false);
				}
			} finally {
				inFlight = false;
			}
		};
		const interval = window.setInterval(check, 3000);
		const timeout = window.setTimeout(
			() => {
				setAwaitingAuthorization(false);
				setError("Jira setup was not confirmed. Please try connecting again.");
			},
			10 * 60 * 1000,
		);
		return () => {
			active = false;
			window.clearInterval(interval);
			window.clearTimeout(timeout);
		};
	}, [awaitingAuthorization, connection.refetch]);

	async function start(requestedSite: string) {
		setError(null);
		let normalizedSite: string;
		try {
			normalizedSite = parseJiraSiteUrl(requestedSite);
		} catch (e) {
			setError(e instanceof Error ? e.message : "Enter a valid Jira site URL.");
			return;
		}

		// Open synchronously from the click so browsers allow the OAuth popup.
		const popup = window.open(
			"about:blank",
			"curo-jira-oauth",
			"popup,width=620,height=760",
		);
		try {
			const result = await connect.mutateAsync(normalizedSite);
			if (new URL(result.url).origin !== "https://auth.atlassian.com")
				throw new Error("Curo returned an invalid Jira authorization URL.");
			previousExpiryRef.current = status?.connected
				? status.webhookExpiresAt
				: null;
			if (popup && !popup.closed) {
				popupRef.current = popup;
				popup.location.assign(result.url);
				setAwaitingAuthorization(true);
			} else {
				window.location.assign(result.url);
			}
		} catch (e) {
			popup?.close();
			setError(
				e instanceof ApiError && e.code === "jira_integration_unavailable"
					? "Jira setup is unavailable. Please try again or ask Curo to check the integration."
					: e instanceof Error
						? e.message
						: "Could not start Jira setup.",
			);
		}
	}

	async function checkInstall() {
		setCheckingInstall(true);
		try {
			await connection.refetch();
		} finally {
			setCheckingInstall(false);
		}
	}

	async function remove() {
		if (!window.confirm("Disconnect this Jira site from your organization?"))
			return;
		setError(null);
		try {
			await disconnect.mutateAsync();
		} catch (e) {
			setError(
				e instanceof ApiError ? e.message : "Could not disconnect Jira.",
			);
		}
	}

	return (
		<Card className="mb-6">
			<CardContent className="flex flex-col gap-4 p-5">
				<div>
					<h2 className="text-body font-semibold text-heading">Jira</h2>
					<p className="mt-1 text-caption text-muted-foreground">
						Research new engineering issues in your Jira Cloud site and post
						concise, source-backed comments.
					</p>
				</div>

				{connection.isLoading ? (
					<Skeleton className="h-10 w-full" />
				) : connection.isError ? (
					<div className="flex items-center gap-3">
						<p className="text-caption text-error">
							Could not load Jira status.
						</p>
						<Button variant="outline" onClick={() => connection.refetch()}>
							Retry
						</Button>
					</div>
				) : status?.connected ? (
					<div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-divider bg-surface3 p-3">
						<div>
							<p className="text-caption font-medium text-heading">
								{expired ? "Webhook expired" : "Connected"}
							</p>
							<p className="text-caption text-muted-foreground">
								{status.siteUrl}
							</p>
							<p className="text-caption text-muted-foreground">
								{expired
									? "Reconnect Jira to resume research on new issues."
									: `Issue webhook expires ${new Date(status.webhookExpiresAt).toLocaleDateString()}.`}
							</p>
						</div>
						{canEdit ? (
							<div className="flex gap-2">
								{expired ? (
									<Button
										onClick={() => start(status.siteUrl)}
										disabled={connect.isPending || awaitingAuthorization}
									>
										Reconnect Jira
									</Button>
								) : null}
								<Button
									variant="outline"
									onClick={remove}
									disabled={disconnect.isPending || awaitingAuthorization}
								>
									{disconnect.isPending ? "Disconnecting…" : "Disconnect"}
								</Button>
							</div>
						) : null}
					</div>
				) : canEdit ? (
					<div className="flex flex-col gap-3">
						<label className="flex flex-col gap-1.5">
							<span className="text-caption font-medium text-paragraph">
								Jira Cloud site URL
							</span>
							<Input
								value={siteUrl}
								onChange={(event) => {
									setSiteUrl(event.target.value);
									setError(null);
								}}
								placeholder="https://your-team.atlassian.net"
								autoComplete="url"
							/>
						</label>
						<Button
							className="self-start"
							onClick={() => start(siteUrl)}
							disabled={connect.isPending || awaitingAuthorization}
						>
							{connect.isPending ? "Opening Jira…" : "Connect Jira"}
						</Button>
					</div>
				) : (
					<p className="text-caption text-muted-foreground">
						Ask an organization admin to connect Jira.
					</p>
				)}

				{!connection.isLoading && !connection.isError && status?.connected ? (
					<JiraForgeStep
						status={status}
						canEdit={canEdit}
						onCheck={checkInstall}
						checking={checkingInstall}
					/>
				) : null}

				{!connection.isLoading &&
				!connection.isError &&
				status?.connected &&
				canEdit ? (
					<>
						<JiraApprovedProjects />
						<JiraWorkHealthSection />
					</>
				) : null}

				{awaitingAuthorization ? (
					<div className="flex flex-wrap items-center gap-3">
						<p className="text-caption text-muted-foreground">
							Finish authorization in the Jira window. This page will update
							automatically.
						</p>
						<Button
							variant="outline"
							onClick={() => {
								popupRef.current?.close();
								popupRef.current = null;
								setAwaitingAuthorization(false);
							}}
						>
							Cancel
						</Button>
					</div>
				) : null}
				{error ? <p className="text-caption text-error">{error}</p> : null}
			</CardContent>
		</Card>
	);
}
